import { css } from 'styled-components';

/* Design tokens.
   ========================================================================
   :root carries the dark theme and is the default, so a document with no
   data-theme attribute renders exactly what the site has always rendered.
   The light theme is an override block keyed on :root[data-theme='light'],
   which means every component keeps using the same variable names and no
   component needs to know a theme exists.

   Both blocks are laid out in the same order under the same headings, so the
   two can be read side by side and a token that is missing an override is
   visible as a gap rather than having to be hunted for.

   Three rules for anyone adding a token:

   1. Anything a component paints with belongs here, not in the component. A
      literal colour in a styled-component is invisible to the theme and will
      look correct in exactly one of the two.

   2. Colours composed at the point of use — the backdrop layers mix a hue
      with a separately tunable strength — are stored as an "-rgb" triple plus
      an alpha, so a theme can change the hue without touching the strength.

   3. Only what genuinely differs goes in the light block. Everything
      geometric is inherited, so the two themes are one layout in different
      light and cannot drift apart structurally. */

const variables = css`
  :root {
    /* ── Surfaces ─────────────────────────────────────────────────────── */
    /* Neutral, no blue cast. --surface-transparent is the panel fill; the
       translucent pair is for the header and sidebar, which blur what is
       behind them. */

    --bg-deep: #08080a;
    --bg: #0a0a0b;
    --surface: #141416;
    --surface-raised: #1e1e21;
    --surface-transparent: #14141680;
    --bg-translucent: rgba(10, 10, 11, 0.65);
    --bg-transparent: rgba(10, 10, 11, 0);
    --border: #26262a;
    --border-strong: #3f3f46;
    --shadow: rgba(0, 0, 0, 0.6);

    /* ── Text ─────────────────────────────────────────────────────────── */
    /* A ladder from brightest to dimmest. Emphasis comes from contrast and
       weight, never from hue. */

    --text: #fafafa;
    --text-emphasis: #e4e4e7;
    --text-secondary: #a1a1aa;
    --text-body: #8a8a93;
    --text-muted: #71717a;
    --text-disabled: #52525b;

    /* The fixed side rails: social icons, the email spine, the hairlines
       under them. Peripheral chrome, and it needs a token of its own because
       the two themes cannot share one. A mid grey on black and the same grey
       inverted onto white measure almost the same contrast ratio, but thin
       dark strokes on a light page read considerably heavier than thin light
       strokes on a dark one, so matching the number gives the wrong result
       and the value has to be set per theme by eye. */

    --rail: #a1a1aa;

    /* ── Accent and semantics ─────────────────────────────────────────── */
    /* One accent, for interactive affordances only: links, focus rings,
       hover. Never decoration. */

    --accent: #64ffda;
    --accent-hover: #9dffe8;
    --accent-tint: rgba(100, 255, 218, 0.1);

    /* Marks a point where the current paradigm cannot follow. Desaturated
       well below a true red, which on a near-black page reads as an error
       state rather than as an argument. Short spans only. */

    --gap: #e8867c;
    --gap-tint: rgba(232, 134, 124, 0.09);

    /* ── Typography ───────────────────────────────────────────────────── */

    --font-sans: 'Calibre', 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;

    --fz-xxs: 12px;
    --fz-xs: 13px;
    --fz-sm: 14px;
    --fz-md: 16px;
    --fz-lg: 18px;
    --fz-xl: 20px;
    --fz-xxl: 22px;
    --fz-heading: 32px;

    /* Line heights by role. Prose is 1.5 everywhere; anything larger than
       body copy is set tighter, because leading that suits 18px opens into
       gaps at 32px and above. Controls sit at 1 so their box height is set by
       padding alone. */

    --lh-display: 1.05;
    --lh-title: 1.15;
    --lh-heading: 1.25;
    --lh-body: 1.3;
    --lh-control: 1;

    /* Calibre's cap-height band sits this far above the centre of its line
       box, so any text centred by flexbox or by symmetric padding reads high.
       Add it to the top inset and subtract it from the bottom. */

    --optical: 0.17em;

    /* Antialiasing is per theme. Light text on a dark page blooms and reads
       heavier; dark text on a light page reads thinner. Greyscale
       antialiasing thins glyphs further, which suits the first case and works
       against the second, so the light theme falls back to the platform
       default and the two end up at the same apparent weight. */

    --font-smoothing: antialiased;
    --font-smoothing-moz: grayscale;

    /* ── Layout ───────────────────────────────────────────────────────── */
    /* Reading measure. Deliberately not in ch units — ch is the width of the
       "0" glyph, much wider than the average character, so 65ch renders
       closer to 88 characters. */

    --measure: 40rem;

    /* Spacing scale. Every gap on the site should come from this list. */

    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 24px;
    --space-6: 32px;
    --space-7: 48px;
    --space-8: 64px;
    --space-9: 96px;
    --space-10: 128px;

    /* Page width, and the gutters inside it. A gutter must clear the fixed
       side rails (40px wide at a 40px inset, 20px inset below 1080px) or the
       content slides under them. */

    --content-width: 1600px;
    --gutter: 150px;
    --gutter-md: 100px;
    --gutter-sm: 50px;
    --gutter-xs: 25px;

    /* Fixed component metrics. The nav has two heights because it shrinks
       once the page is scrolled. */

    --border-radius: 6px;
    --nav-height: 100px;
    --nav-scroll-height: 80px;
    --tab-height: 42px;
    --tab-width: 120px;
    --hamburger-width: 30px;

    /* ── Motion ───────────────────────────────────────────────────────── */
    /* One easing curve for the whole site. The two shorthands exist because
       most transitions are "all" at one of two durations. */

    --easing: cubic-bezier(0.645, 0.045, 0.355, 1);
    --transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    --transition-slow: all 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);

    /* The hamburger's two bars, which have to move on different schedules
       opening and closing or the cross assembles in the wrong order. */

    --ham-before: top 0.1s ease-in 0.25s, opacity 0.1s ease-in;
    --ham-before-active: top 0.1s ease-out, opacity 0.1s ease-out 0.12s;
    --ham-after: bottom 0.1s ease-in 0.25s, transform 0.22s cubic-bezier(0.55, 0.055, 0.675, 0.19);
    --ham-after-active: bottom 0.1s ease-out,
      transform 0.22s cubic-bezier(0.215, 0.61, 0.355, 1) 0.12s;

    /* ── Backdrop ─────────────────────────────────────────────────────── */
    /* Three fixed layers behind the page, drawn by components/backdrop.js.
       Nothing animates on its own; the only movement is the pointer's.

       Strength and hue are separate tokens on purpose. Every layer adds light
       on a near-black page, and on a near-white one the vignette and the
       lattice have to take light away instead, which is a change of hue
       rather than of amount. */

    --fx-grain: 0.028;
    --fx-halo: 0.02;
    --fx-lattice: 0.1;
    --fx-depth-light: 0.07;
    --fx-depth-vignette: 0.5;

    --fx-depth-light-rgb: 120, 130, 160;
    --fx-depth-vignette-rgb: 0, 0, 0;
    --fx-lattice-rgb: 255, 255, 255;
    --fx-halo-rgb: 255, 255, 255;

    --fx-halo-radius: 500px;
    --fx-lattice-radius: 190px;

    /* Grain over a dark page is light speckle and composites normally; over a
       light page it has to darken. */

    --fx-grain-blend: normal;

    /* ── Glass ────────────────────────────────────────────────────────── */
    /* Both are no-ops in the dark theme, where surfaces are opaque panels
       rather than sheets with the page showing through, so every rule
       carrying them is inert here and costs nothing. */

    --glass-blur: none;
    --glass-shadow: none;

    /* ── Runtime ──────────────────────────────────────────────────────── */
    /* Written by backdrop.js on pointer move, once per frame. The defaults
       put the pointer-driven layers at the viewport centre, so they read as
       ambient light on load rather than as an effect parked in a corner. */

    --fx-x: 50vw;
    --fx-y: 45vh;
  }

  /* ══ Light theme ═══════════════════════════════════════════════════════
     Glass over a cool near-white, not a white page with dark text on it.
     Three things carry that:

     - The page is never #fff. Panels are white, so a surface is lighter than
       what surrounds it, which is what makes it read as a sheet lying on the
       page rather than a hole cut into it.
     - Those panels are translucent and blurred, so the grain, the lattice and
       the pointer halo show through them softly. On a dark page a panel hides
       what is behind it; here it filters it.
     - The lift comes from a wide, very soft shadow rather than from a border.

     The accent cannot simply be darkened. #64ffda on near-white is about
     1.4:1 and unreadable, and taken down by lightness alone it turns into a
     near-black green that reads as text rather than as an accent, so this
     drops the red channel instead and keeps chroma. */

  :root[data-theme='light'] {
    /* ── Surfaces ─────────────────────────────────────────────────────── */
    /* The page sits a shade or two off white and every panel on it is pure
       white. That gap is doing the work: it is what makes a card read as a
       sheet, and what gives the grain and the pointer halo something to be
       visible against.

       The borders are contrast matched to the dark theme rather than picked
       by eye. A border against its page measures 1.31:1 on dark and these
       land on 1.29:1 and 1.91:1, against the dark theme's 1.31 and 1.89. An
       earlier pair sat at 1.17 and 1.80, which is why the same 1px rules
       looked a hair thinner here than they do on dark. */

    --bg-deep: #dee2e9;
    --bg: #f3f4f7;
    --surface: #ffffff;
    --surface-raised: #ffffff;
    --surface-transparent: rgba(255, 255, 255, 0.45);
    --bg-translucent: rgba(240, 242, 246, 0.62);
    --bg-transparent: rgba(240, 242, 246, 0);
    --border: #ccd1da;
    --border-strong: #a4acbb;
    --shadow: rgba(23, 31, 51, 0.16);

    /* ── Text ─────────────────────────────────────────────────────────── */
    /* The ladder inverts: --text is now the darkest rather than the
       brightest. Every step was checked against --bg, and the three carrying
       running text clear 4.5:1. */

    --text: #101319;
    --text-emphasis: #1d222c;
    --text-secondary: #3f4653;
    --text-body: #545c6b;
    --text-muted: #5f6675;
    --text-disabled: #7c8595;

    --rail: #767e8d;

    /* ── Accent and semantics ─────────────────────────────────────────── */

    --accent: #009974;
    --accent-hover: #007a5c;
    --accent-tint: rgba(0, 153, 116, 0.12);

    --gap: #b83a30;
    --gap-tint: rgba(184, 58, 48, 0.08);

    /* ── Typography ───────────────────────────────────────────────────── */

    --font-smoothing: subpixel-antialiased;
    --font-smoothing-moz: auto;

    /* ── Backdrop ─────────────────────────────────────────────────────── */
    /* Grain multiplies rather than adds, since over a light page it has to
       take light away. The lattice inverts to a dark ink so the dots are
       still dots, and runs stronger than on dark because a 1px dot has very
       little area and subtracting light from a near-white page is a smaller
       change than adding it to a near-black one.

       Light from above becomes a white sky wash; the vignette becomes a cool
       grey settling at the bottom, since black would read as dirt. */

    --fx-grain: 0.028;
    --fx-halo: 0.05;
    --fx-lattice: 0.2;
    --fx-depth-light: 0.55;
    --fx-depth-vignette: 0.4;

    --fx-depth-light-rgb: 255, 255, 255;
    --fx-depth-vignette-rgb: 146, 157, 182;
    --fx-lattice-rgb: 36, 48, 74;
    --fx-halo-rgb: 102, 255, 218;

    --fx-grain-blend: multiply;

    /* ── Glass ────────────────────────────────────────────────────────── */

    --glass-blur: blur(14px) saturate(160%);
    --glass-shadow: 0 1px 2px rgba(23, 31, 51, 0.05), 0 12px 32px -18px rgba(23, 31, 51, 0.55);
  }
`;

export default variables;
